import React from "react";

// =============================================================
// Tipagens compartilhadas
// =============================================================
export type HistoricoItem = {
  id: number;
  produto_id: number;
  movimentacao_id?: number;
  tipo?:
    | "entrada"
    | "saida"
    | "perda"
    | "venda"
    | "consumo"
    | "retorno"
    | string
    | null;
  /**
   * Para compat com outros widgets, esta célula costuma exibir PREÇO.
   * Aqui ela será tratada como preço de venda se `preco_venda` não for enviado.
   */
  custo_unitario: string | number | null;
  quantidade: number | null;
  data_movimentacao: string | null;
  responsavel_id: number | null;
  responsavel_nome?: string | null;

  // Margens
  margem_percentual?: number | null; // margem "genérica" da linha
  margem_mov_percentual?: number | null; // margem específica desta movimentação

  // Campos para cálculo explícito
  custo_referencia?: number | string | null; // custo vigente para calcular a margem
  preco_venda?: number | string | null; // preço de venda desta linha (prioridade)

  // (opcional) moeda por linha; usada apenas pelos formatadores externos
  moeda?: string | null;
};

export type HistoricoPrecosWidgetProps = {
  items: HistoricoItem[];
  title?: string;
  loading?: boolean;
  error?: string | null;
  formatCurrency?: (v: string | number | null | undefined) => string;
  formatDateTime?: (iso: string | null | undefined) => string;
  onRowClick?: (item: HistoricoItem) => void;
};

/** ====== Tipos para histórico de preços de venda (API diretoria) ====== */
export type PrecoVendaItem = {
  id: number;
  data_definicao: string; // ISO
  definido_por_id: number | null;
  definido_por_nome?: string | null;
  margem_percentual: number | null;
  preco_venda: number | string | null;
  custo_referencia?: number | string | null; // se disponível na API
  margem_mov_percentual?: number | null; // se disponível na API
  moeda?: string | null; // se disponível
};

// ======================== Default formatters ======================
const defaultFormatCurrency = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
};

const defaultFormatDateTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const data = d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const hora = d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${data} - ${hora}`;
};

// ============================= Layout =============================
//            0             1      2      3       4        5        6
const COLUMNS = [
  "Responsável",
  "Q.",
  "Tipo",
  "Preço",
  "Margem",
  "Variação",
  "Data",
] as const;

// Ajuste o grid p/ 7 colunas (inclui coluna de "Margem")
const gridTemplate = "2fr 0.8fr 1fr 1.1fr 1fr 1fr 1.5fr";

// Paleta pastel clara — AUMENTOU = VERDE, DIMINUIU = VERMELHO
const BG_UP = "#EAF7EA"; // aumentou (verde pastel)
const BG_DOWN = "#FDEAEA"; // diminuiu (vermelho pastel)
const BG_FLAT = "#FFF9DB"; // igual (amarelo pastel)
const BG_BASE = "#E7F1FF"; // ponto de partida (azul pastel)

// Acentos (apenas na 1ª célula da linha)
const ACCENT_RED = "#D66";
const ACCENT_GREEN = "#7BBF7B";
const ACCENT_YELLOW = "#E5C100";
const ACCENT_BLUE = "#4A90E2";

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: {
    border: "1px solid #000",
    width: "100%",
    marginTop: 30,
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
  },
  top: {
    height: 35,
    backgroundColor: "#124280",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    borderBottom: "1px solid #000",
  },
  legendWrap: {
    padding: "10px 12px",
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    borderBottom: "1px solid #000",
    background: "#fafafa",
    fontSize: 12,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#333",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  swatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.15)",
    boxSizing: "border-box",
  },
  headerRow: {
    height: 35,
    backgroundColor: "#124280CF",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "center",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    boxSizing: "border-box",
    borderBottom: "1px solid #000",
  },
  headerCell: {
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
    borderRight: "1px solid #000",
  },
  body: {
    backgroundColor: "#fff",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "stretch",
    fontSize: 13,
    boxSizing: "border-box",
  },
  bodyCell: {
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#333",
    boxSizing: "border-box",
    borderRight: "1px solid #000",
  },
  error: {
    padding: 12,
    color: "#c0362c",
    fontSize: 13,
    borderTop: "1px solid #000",
  },
};

// ============================= Utils ==============================
const parsePrice = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).trim();
  // tenta tratar números do tipo "1.234,56" e "1,234.56"
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  let norm = s;
  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    norm =
      lastComma > lastDot
        ? s.replace(/\./g, "").replace(",", ".")
        : s.replace(/,/g, "");
  } else if (hasComma) {
    norm = s.replace(",", ".");
  }
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
};

function capitalizeTipo(tipo?: string | null) {
  if (!tipo) return "—";
  return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

function getRowBackgroundByTrend(
  trend?: "up" | "down" | "flat" | "na"
): string | undefined {
  if (trend === "up") return BG_UP;
  if (trend === "down") return BG_DOWN;
  if (trend === "flat") return BG_FLAT;
  if (trend === "na") return BG_BASE; // ponto de partida
  return undefined;
}

function getLeftBorderByTrend(
  trend?: "up" | "down" | "flat" | "na"
): string | undefined {
  if (trend === "up") return `4px solid ${ACCENT_GREEN}`; // ↑ aumentou = verde
  if (trend === "down") return `4px solid ${ACCENT_RED}`; // ↓ diminuiu = vermelho
  if (trend === "flat") return `4px solid ${ACCENT_YELLOW}`;
  if (trend === "na") return `4px solid ${ACCENT_BLUE}`; // ponto de partida
  return undefined;
}

// =========================== Component (Sell - tabela base) ============================
const HistoricoPrecosSellWidget: React.FC<HistoricoPrecosWidgetProps> = ({
  items,
  title = "Histórico de Preços de Venda do Produto",
  loading = false,
  error = null,
  formatCurrency = defaultFormatCurrency,
  formatDateTime = defaultFormatDateTime,
  onRowClick,
}) => {
  const showEmpty = !loading && (!items || items.length === 0);

  // Ordena por data (antigas -> novas) para calcular variação corretamente
  const chronological = React.useMemo(() => {
    const arr = (items ?? [])
      .map((it) => {
        const sell = parsePrice(it.preco_venda ?? it.custo_unitario); // prioriza preco_venda
        const cost = parsePrice(it.custo_referencia);
        return {
          ...it,
          _price: sell, // usado para célula de preço e variação
          _sell: sell, // explícito p/ cálculo de margem
          _costRef: cost, // custo de referência
        };
      })
      .sort((a, b) => {
        const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
        const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
        return ta - tb; // antigas -> novas
      }) as (HistoricoItem & {
      _price: number | null;
      _sell: number | null;
      _costRef: number | null;
      _trend?: "up" | "down" | "flat" | "na";
      _pct?: number | null;
    })[];

    let prev: number | null = null;
    for (const r of arr) {
      const p = r._price;
      if (prev === null || prev === 0 || p === null) {
        r._trend = "na";
        r._pct = null;
      } else {
        const pct = ((p - prev) / prev) * 100;
        if (Math.abs(pct) < 1e-10) {
          r._trend = "flat";
          r._pct = 0;
        } else if (pct > 0) {
          r._trend = "up";
          r._pct = pct;
        } else {
          r._trend = "down";
          r._pct = pct;
        }
      }
      if (p !== null) prev = p;
    }
    return arr;
  }, [items]);

  // Exibir (novas -> antigas)
  const rows = React.useMemo(() => {
    return [...chronological].sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
      return tb - ta;
    });
  }, [chronological]);

  const renderDelta = (pct: number | null | undefined, trend?: string) => {
    if (trend === "na") return "N/A";
    if (pct == null) return "—";
    const arrow = pct > 0 ? "↑" : pct < 0 ? "↓" : "→";
    const sign = pct > 0 ? "+" : "";
    return `${arrow} ${sign}${pct.toFixed(2)}%`;
  };

  const renderMargin = (row: {
    margem_mov_percentual?: number | null;
    margem_percentual?: number | null;
    _sell?: number | null;
    _costRef?: number | null;
  }) => {
    // (1) margem específica da movimentação
    const m1 = row.margem_mov_percentual;
    if (m1 != null && Number.isFinite(m1)) return `${Number(m1).toFixed(2)}%`;

    // (2) margem "genérica" da linha
    const m2 = row.margem_percentual;
    if (m2 != null && Number.isFinite(m2)) return `${Number(m2).toFixed(2)}%`;

    // (3) fallback: calcula a partir do preço e custo de referência
    const p = row._sell ?? null;
    const c = row._costRef ?? null;
    if (p != null && c != null && c > 0) {
      const m = ((p - c) / c) * 100;
      return `${m.toFixed(2)}%`;
    }

    return "—";
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.top}>{title}</div>

      {/* Legenda / Explicação */}
      <div style={styles.legendWrap} aria-label="Legenda de variações de preço">
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_UP }} />
          <span>
            <strong>↑ Aumentou</strong> (verde pastel)
          </span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_DOWN }} />
          <span>
            <strong>↓ Diminuiu</strong> (vermelho pastel)
          </span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_FLAT }} />
          <span>
            <strong>→ Igual</strong> (amarelo pastel)
          </span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_BASE }} />
          <span>
            <strong>Ponto de partida</strong> (variação N/A)
          </span>
        </div>
      </div>

      {/* Cabeçalho */}
      <div style={styles.headerRow}>
        {COLUMNS.map((label, idx) => (
          <div
            key={label}
            style={{
              ...styles.headerCell,
              borderRight:
                idx === COLUMNS.length - 1 ? "none" : "1px solid #000",
            }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* Corpo */}
      <div style={styles.body}>
        {showEmpty
          ? Array.from({ length: COLUMNS.length }).map((_, idx) => (
              <div
                key={`empty-${idx}`}
                style={{
                  ...styles.bodyCell,
                  borderRight:
                    idx === COLUMNS.length - 1 ? "none" : "1px solid #000",
                }}
              >
                —
              </div>
            ))
          : rows.map((c, rowIdx) => {
              const bg = getRowBackgroundByTrend((c as any)._trend);
              const leftBorder = getLeftBorderByTrend((c as any)._trend);

              const priceCell = formatCurrency
                ? formatCurrency((c as any)._price)
                : defaultFormatCurrency((c as any)._price);

              const cells: (string | number)[] = [
                c.responsavel_nome || (c.responsavel_id ?? "—"),
                c.quantidade ?? "—",
                capitalizeTipo(c.tipo),
                priceCell,
                renderMargin(c as any),
                renderDelta((c as any)._pct ?? null, (c as any)._trend),
                (c.data_movimentacao && formatDateTime
                  ? formatDateTime(c.data_movimentacao)
                  : defaultFormatDateTime(c.data_movimentacao)) || "—",
              ];

              return cells.map((val, colIdx) => (
                <div
                  key={`${c.id}-${colIdx}`}
                  onClick={onRowClick ? () => onRowClick!(c) : undefined}
                  style={{
                    ...styles.bodyCell,
                    background: bg,
                    borderLeft:
                      colIdx === 0 && leftBorder ? leftBorder : "none",
                    borderRight:
                      colIdx === COLUMNS.length - 1 ? "none" : "1px solid #000",
                    borderTop: rowIdx === 0 ? "none" : "1px solid #000",
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                  title={
                    (c as any)._trend === "na"
                      ? "Ponto de partida (sem base anterior)"
                      : `Variação: ${(c as any)._pct?.toFixed(2)}%`
                  }
                >
                  {val}
                </div>
              ));
            })}
      </div>

      {error && (
        <div style={styles.error}>Erro ao carregar histórico: {error}</div>
      )}
    </div>
  );
};

// =========================== Component (Venda - Adaptador) ============================
export const HistoricoPrecosVendaWidget: React.FC<{
  items: PrecoVendaItem[];
  title?: string;
  loading?: boolean;
  error?: string | null;
  formatCurrency?: (v: string | number | null | undefined) => string;
  formatDateTime?: (iso: string | null | undefined) => string;
  onRowClick?: (item: HistoricoItem) => void;
}> = ({
  items,
  title = "Histórico de Preços de Venda",
  loading,
  error,
  formatCurrency,
  formatDateTime,
  onRowClick,
}) => {
  // mapeia PrecoVendaItem -> HistoricoItem (mantém campos para margem por linha)
  const mapped: HistoricoItem[] = (items ?? []).map((r) => ({
    id: r.id,
    produto_id: 0, // não utilizado no visual
    movimentacao_id: undefined,
    tipo: "venda",

    // preço mostrado na coluna "Preço"
    custo_unitario: r.preco_venda, // compat
    preco_venda: r.preco_venda, // explícito

    // custo de referência (se vier)
    custo_referencia: r.custo_referencia ?? null,

    quantidade: null,
    data_movimentacao: r.data_definicao,

    responsavel_id: r.definido_por_id ?? null,
    responsavel_nome: r.definido_por_nome ?? null,

    // margens
    margem_percentual: r.margem_percentual ?? null, // fallback
    margem_mov_percentual: r.margem_mov_percentual ?? null,

    moeda: r.moeda ?? null,
  }));

  return (
    <HistoricoPrecosSellWidget
      items={mapped}
      title={title}
      loading={loading}
      error={error}
      formatCurrency={formatCurrency}
      formatDateTime={formatDateTime}
      onRowClick={onRowClick}
    />
  );
};

export default HistoricoPrecosSellWidget;
